import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Agenda from './pages/Agenda';
import Speakers from './pages/Speakers';
import Streaming from './pages/Streaming';
import Profile from './pages/Profile';
import Scanner from './pages/Scanner';
import GalleryPage from './pages/GalleryPage';
import StaffQuestions from './pages/StaffQuestions';
import StaffGallery from './pages/StaffGallery';
import EventGallery from './pages/EventGallery';
import WelcomeLetter from './pages/WelcomeLetter';
import ThankYouLetter from './pages/ThankYouLetter';
import Certificate from './pages/Certificate';
import UserQuestions from './pages/UserQuestions';
import InteractivePoll from './pages/InteractivePoll';
import StaffPollManagement from './pages/StaffPollManagement';
import StaffPollResults from './pages/StaffPollResults';
import Survey from './pages/Survey';
import DynamicPage from './pages/DynamicPage';

import CircuitMapPage from './pages/CircuitMapPage';
import QRPointsScanner from './pages/QRPointsScanner';
import Concentrese from './pages/Concentrese';
import Laberinto from './pages/Laberinto';

import StaffQRGenerator from './pages/StaffQRGenerator';
import Podium from './pages/Podium';
import StationSelfiePage from './pages/StationSelfiePage';

// Protected Route Component

const ProtectedRoute = ({ children, pageKey, allowRoles }) => {
    const { user, loading: authLoading } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const location = useLocation();

    if (authLoading || configLoading) return (
        <div className="h-screen w-full flex flex-col items-center justify-center bg-white">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <span className="font-black text-[10px] uppercase tracking-[0.3em] text-slate-300"> Loading</span>
        </div>
    );

    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

    // Check if the page is disabled in config for regular users
    const isStaffOrAdmin = user && ['staff', 'admin'].includes(user.role);
    if (pageKey && !isStaffOrAdmin) {
        // If pageKey is explicitly false or not enabled when default is false (like podium)
        if (pageKey === 'podium' && config?.pages?.podium !== true) {
            return <Navigate to="/" />;
        } else if (config?.pages?.[pageKey] === false) {
            return <Navigate to="/" />;
        }
    }

    // Role check
    if (allowRoles && !allowRoles.includes(user.role)) {
        return <Navigate to="/" />;
    }

    return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
    const { config } = useConfig();

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/survey" element={<Layout><Survey /></Layout>} />

                {/* Protected Routes */}
                <Route path="/" element={<ProtectedRoute pageKey="home"><Home /></ProtectedRoute>} />
                <Route path="/circuito" element={<ProtectedRoute><CircuitMapPage /></ProtectedRoute>} />
                <Route path="/podio" element={<ProtectedRoute pageKey="podium"><Podium /></ProtectedRoute>} />
                <Route path="/selfie" element={<ProtectedRoute><StationSelfiePage /></ProtectedRoute>} />
                <Route path="/concentrese" element={<ProtectedRoute><Concentrese /></ProtectedRoute>} />
                <Route path="/laberinto" element={<ProtectedRoute><Laberinto /></ProtectedRoute>} />
                <Route path="/escanear-puntos" element={<ProtectedRoute><QRPointsScanner /></ProtectedRoute>} />
                <Route path="/agenda" element={<ProtectedRoute pageKey="agenda"><Agenda /></ProtectedRoute>} />
                <Route path="/speakers" element={<ProtectedRoute pageKey="speakers"><Speakers /></ProtectedRoute>} />
                <Route path="/streaming" element={<ProtectedRoute pageKey="streaming"><Streaming /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute pageKey="profile"><Profile /></ProtectedRoute>} />
                <Route path="/scanner" element={<ProtectedRoute allowRoles={['staff', 'admin']}><Scanner /></ProtectedRoute>} />
                <Route path="/staff-qr-generator" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffQRGenerator /></ProtectedRoute>} />



                <Route path="/staff-questions" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffQuestions /></ProtectedRoute>} />
                <Route path="/staff-gallery" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffGallery /></ProtectedRoute>} />
                <Route path="/event-gallery" element={<ProtectedRoute><EventGallery /></ProtectedRoute>} />
                <Route path="/welcome" element={<ProtectedRoute pageKey="welcome"><WelcomeLetter /></ProtectedRoute>} />
                <Route path="/thanks" element={<ProtectedRoute pageKey="thanks"><ThankYouLetter /></ProtectedRoute>} />
                <Route path="/certificate" element={<ProtectedRoute pageKey="certificate"><Certificate /></ProtectedRoute>} />
                <Route path="/questions-expert" element={<ProtectedRoute pageKey="questions_private"><UserQuestions /></ProtectedRoute>} />
                <Route path="/interactiva" element={<ProtectedRoute><InteractivePoll /></ProtectedRoute>} />
                <Route path="/interactiva-gestion" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollManagement /></ProtectedRoute>} />
                <Route path="/interactiva-stats/:id" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollResults /></ProtectedRoute>} />

                {/* Dynamic Gallery Routes */}
                {config?.customGalleryPages?.map(page => (
                    <Route
                        key={page.slug}
                        path={`/gallery/${page.slug}`}
                        element={<ProtectedRoute><GalleryPage /></ProtectedRoute>}
                    />
                ))}

                {/* Custom CMS Pages */}
                <Route path="/p/:slug" element={<ProtectedRoute><DynamicPage /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Router>
    );
};

function App() {
    return (
        <ConfigProvider>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </ConfigProvider>
    );
}

export default App;
